import { type ReactNode, useEffect } from 'react';
import styles from './SlidePanel.module.css';

interface SlidePanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: number;
  actions?: ReactNode;
}

export function SlidePanel({ open, onClose, title, children, width = 700, actions }: SlidePanelProps) {
  // ESC キーで閉じる
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.panel} style={{ width }}>
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={onClose}>←</button>
          <h2 className={styles.title}>{title}</h2>
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
        <div className={styles.body}>{children}</div>
      </div>
    </>
  );
}

/* サブコンポーネント: dp-sec, spec-grid を React で使えるように export */
export function PanelSection({ title, children }: { title?: string | ReactNode; children: ReactNode }) {
  return (
    <div className={styles.section}>
      {title && <div className={styles.sectionTitle}>{title}</div>}
      {children}
    </div>
  );
}

export function SpecGrid({ children }: { children: ReactNode }) {
  return <div className={styles.specGrid}>{children}</div>;
}

export function SpecCell({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className={styles.specCell}>
      <div className={styles.specLabel}>{label}</div>
      <div className={`${styles.specValue} ${mono ? styles.specMono : ''}`}>{value}</div>
    </div>
  );
}

export function ModuleRow({ icon, name, desc, children }: { icon: string; name: string; desc?: string; children?: ReactNode }) {
  return (
    <div className={styles.modRow}>
      <div className={styles.modIcon}>{icon}</div>
      <div className={styles.modInfo}>
        <div className={styles.modName}>{name}</div>
        {desc && <div className={styles.modDesc}>{desc}</div>}
      </div>
      {children}
    </div>
  );
}

export function AuditRow({ time, event, ip }: { time: string; event: string; ip: string }) {
  return (
    <div className={styles.auditRow}>
      <div className={styles.auditTime}>{time}</div>
      <div className={styles.auditEvent} dangerouslySetInnerHTML={{ __html: event }} />
      <div className={styles.auditIp}>{ip}</div>
    </div>
  );
}
